/**
 * MotionPipelineList — Motions page tab 2 ("Pipeline List"), Figma 1956:24237.
 *
 * A CROSS-Motion view of tagged Opps: a top "Information (Tagged only)" card of
 * big-number SFDC-stage KPIs + a "Pipeline List (Tagged only)" detail table, one
 * row per tagged Opp. Backed by ListTaggedPipelines (DAT-8462 / CR-291680700).
 *   - stageCounts → Information KPIs (7 SFDC stages, backend-aggregated; NOT the
 *     IVT motion_stage funnel).
 *   - items → detail table (Account / Opp / Motion / Use Cases / SFDC Stage / ARR
 *     / Owner / Tags). Every row is tagged.
 *
 * Filters: the shared <MotionFilters> row (Industry / Use Case / Motion / Topic /
 * Asset), all multi-select + server-side (industries / useCases /
 * motionIds / subTopicIds / assetIds, OR semantics) → refetch. Motion options
 * come from ListMotions; the other options are fetched inside MotionFilters.
 *
 * lastStageChangeTime / activityCount come back null / 0 (backend §4.7) — not
 * surfaced as columns yet.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CollectionPreferences,
  type CollectionPreferencesProps,
  Header,
  Link,
  Pagination,
  SpaceBetween,
  Table,
  type TableProps,
} from '@amzn/awsui-components-react';
import {
  getIvtClient,
  ListMotionsCommand,
  ListAssetsCommand,
  ListTaggedPipelinesCommand,
  type PipelineListItem,
  type SfdcStageCounts,
} from '../../../client/IvtHubClient';
import type { PropertyFilterProps } from '@amzn/awsui-components-react';
import MotionFilters, { type MotionFilterOption } from './MotionFilters';
import PipelinePropertyFilter from './PipelinePropertyFilter';
import { ownIndustryOnlyQuery } from '../../../components/pipelineFilterShared';
import { useMotionFilterState, PIPELINE_PROPERTY_FILTER_STORAGE_KEY } from '../useMotionFilterState';
import { usePipelineFilterOptions } from '../usePipelineFilterOptions';
import { queryToPropertyFilterParam } from '../../../utils/sfdcPipelinePropertyFilter';
import { oppSfdcUrl } from '../../../utils/sfdc';
import { resolveIndustryQueryScope, isEmptyIndustryScope } from '../../../client/permissionClient';
import MotionActivityPopup, { activityCountLabel } from './MotionActivityPopup';
import OverflowChips from './OverflowChips';
import { scoreStyleWithZero } from '../../../utils/scoreStyle';
import { reconcileVisibleContent } from '../../../utils/tablePrefs';
import './MotionPipelineList.css';

/** Empty PropertyFilter query (initial + reset). */
const EMPTY_QUERY: PropertyFilterProps.Query = { operation: 'and', tokens: [], tokenGroups: [] };

// sessionStorage key for the persisted SFDC property-filter query (survives tab
// switch / detail round-trip, like the other Pipeline-tab filters). Defined in
// useMotionFilterState (single source) so setOnlyAssetFilter can clear it too.
const PROPERTY_FILTER_STORAGE_KEY = PIPELINE_PROPERTY_FILTER_STORAGE_KEY;

/**
 * Initial property-filter query: whatever the session has, else PM's default of
 * own-industry rows only (2026-09-07).
 *
 * ⚠️ The default is seeded ONLY when the key is ABSENT. It must NOT be seeded when the key
 * EXISTS but holds an empty query, because that is a user who deliberately CLEARED the
 * filter, and `setPropertyQuery` persists on every change so that state is real. Both cases
 * previously funnelled into the same `EMPTY_QUERY` return, so seeding there would resurrect
 * the token on every tab switch and detail round-trip.
 *
 * ABSENT therefore means "no choice recorded in this tab yet" — NOT "first visit". A cleared
 * filter survives a page RELOAD too, because sessionStorage is per-tab and outlives F5; only
 * a new tab starts over. And `setOnlyAssetFilter` deliberately writes an empty query rather
 * than removing the key, precisely so its clear is not read as absence.
 */
function loadPropertyFilterQuery(): PropertyFilterProps.Query {
  try {
    const raw = sessionStorage.getItem(PROPERTY_FILTER_STORAGE_KEY);
    // Key absent = first visit this session → apply the default.
    if (raw == null) return ownIndustryOnlyQuery();
    if (!raw) return EMPTY_QUERY;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return EMPTY_QUERY;
    // Accept if EITHER tokens or tokenGroups is a valid array (a grouped query has
    // tokens:[] + tokenGroups:[...]; validating only tokens would discard it). Coerce
    // each to an array so a malformed field can't reach the control as a non-array.
    const tokens = Array.isArray(parsed.tokens) ? parsed.tokens : [];
    const tokenGroups = Array.isArray(parsed.tokenGroups) ? parsed.tokenGroups : [];
    if (tokens.length === 0 && tokenGroups.length === 0) return EMPTY_QUERY;
    return { operation: parsed.operation === 'or' ? 'or' : 'and', tokens, tokenGroups };
  } catch {
    /* ignore corrupt storage */
  }
  return EMPTY_QUERY;
}

/** Information KPI cells in display order (7 SFDC stages), each with a top-border
 *  accent colour (aligned to the sfdcStage palette used elsewhere). */
const STAGE_CELLS: Array<{ key: keyof SfdcStageCounts; label: string; accent: string }> = [
  { key: 'prospect', label: 'Prospect', accent: '#06b6d4' },
  { key: 'qualified', label: 'Qualified', accent: '#22c55e' },
  { key: 'techValidation', label: 'Tech Validation', accent: '#ef4444' },
  { key: 'bizValidation', label: 'Biz Validation', accent: '#3b82f6' },
  { key: 'committed', label: 'Committed', accent: '#a855f7' },
  { key: 'launched', label: 'Launched', accent: '#84cc16' },
  { key: 'others', label: 'Others', accent: '#94a3b8' },
];

function fmtArr(arr?: number): string {
  if (arr == null) return '—';
  return `$${Math.round(arr).toLocaleString('en-US')}`;
}

// ── Pagination + preferences ─────────────────────────────────────────────────
//
// Two layers of "page size":
//   * FETCH_CHUNK — how many rows we pull from the server per keyset request. A
//     larger chunk decouples the UI page from the network: flipping to an
//     already-loaded UI page is instant; only crossing the loaded frontier hits
//     the backend. (backend maxResults range 1–500.)
//   * UI page size — how many rows the table shows per page, user-set via the
//     preferences gear (10 / 20 / 50). Purely a client-side slice of loaded rows.
// INVARIANT: FETCH_CHUNK must be >= the largest UI page size, so one fetch always
// fills at least one whole page (the loadedPages floor/ceil logic below relies on
// it — a chunk smaller than a page could leave the target page still out of range
// after a fetch, causing a clamp bounce).
//
// 100 is the backend's own default page size (maxResults default 100, cap 500).
// It also raises the ceiling under which client-side column sort is EXACT: sort
// only reorders LOADED rows, so a single 100-row fetch keeps sort accurate for
// any filtered set that fits in one chunk (the common case) — see the sort hint.
const FETCH_CHUNK = 100;

const PAGE_SIZE_OPTIONS: CollectionPreferencesProps.PageSizeOption[] = [
  { value: 10, label: '10 条 / 页' },
  { value: 20, label: '20 条 / 页' },
  { value: 50, label: '50 条 / 页' },
];

/** Every column id, in display order. Single source of truth for the table and
 *  the "Visible columns" preference list. */
const COLUMN_IDS = [
  'account',
  'opp',
  'motion',
  'assets',
  'useCases',
  'topics',
  'stage',
  'arr',
  'owner',
  'score',
  'activities',
  'tags',
] as const;

const COLUMN_LABELS: Record<(typeof COLUMN_IDS)[number], string> = {
  account: 'Account',
  opp: 'Opportunity',
  motion: 'Motions',
  assets: 'Assets',
  useCases: 'Use Cases',
  topics: 'Topics',
  stage: 'SFDC Stage',
  arr: 'ARR',
  owner: 'Owner',
  score: 'Engagement Depth Score',
  activities: 'Activities',
  tags: 'Tags',
};

// Per-column minimum width (px). Cloudscape requires numeric minWidth on
// resizable columns (defaults to 120) — set explicitly so a drag can't collapse
// a column to nothing. Chip/link columns get a touch more room.
const COLUMN_MIN_WIDTH: Record<(typeof COLUMN_IDS)[number], number> = {
  account: 160,
  opp: 200,
  motion: 180,
  assets: 160,
  useCases: 160,
  topics: 180,
  stage: 130,
  arr: 110,
  owner: 110,
  score: 90,
  activities: 120,
  tags: 160,
};

// 'account' is the row header (isRowHeader) — Cloudscape requires it always
// present, so it is NOT user-hideable (excluded from the options + always kept
// in visibleColumns). Only the rest are toggleable.
const ROW_HEADER_ID = 'account';
const HIDEABLE_COLUMN_IDS = COLUMN_IDS.filter((id) => id !== ROW_HEADER_ID);

const VISIBLE_CONTENT_OPTIONS: CollectionPreferencesProps.VisibleContentOptionsGroup[] = [
  { label: 'Pipeline columns', options: HIDEABLE_COLUMN_IDS.map((id) => ({ id, label: COLUMN_LABELS[id] })) },
];

const DEFAULT_PREFERENCES: CollectionPreferencesProps.Preferences = {
  pageSize: 20,
  visibleContent: [...HIDEABLE_COLUMN_IDS], // all hideable columns shown by default
};

const PREFS_STORAGE_KEY = 'ivt_motion_pipeline_table_prefs';

// One-time frozen baseline: the hideable columns as they existed the moment the
// new-column-default-visible fix shipped — i.e. everything EXCEPT 'score'. Used
// only to reconcile prefs saved BEFORE the fix (which carry no knownColumns
// snapshot): a column not in this baseline is genuinely new and defaults to
// visible; a baseline column the user dropped stays hidden. NEVER edit this again
// — later saves write their own knownColumns snapshot; new columns are handled
// automatically by reconcileVisibleContent, not by touching this constant.
const LEGACY_KNOWN_COLUMNS: readonly string[] = HIDEABLE_COLUMN_IDS.filter((id) => id !== 'score');

/** Load + SANITIZE persisted prefs: reject a non-positive/non-integer pageSize
 *  and reconcile the stored visible-column set with the current columns so a
 *  newly-added column defaults to VISIBLE for returning users while intentional
 *  hides are preserved (see reconcileVisibleContent). */
function loadPreferences(): CollectionPreferencesProps.Preferences {
  try {
    const stored = localStorage.getItem(PREFS_STORAGE_KEY);
    if (!stored) return DEFAULT_PREFERENCES;
    // Strip the persistence-only knownColumns snapshot so it doesn't leak into the
    // in-memory Cloudscape preferences object (rest = the real Preferences shape).
    const { knownColumns, ...rest } = JSON.parse(stored) as CollectionPreferencesProps.Preferences & {
      knownColumns?: string[];
    };
    // Only reject pageSize values that would break the pagination math (<=0 or
    // non-integer). An unusual-but-positive size (e.g. from an older options set)
    // is harmless and preserved, so we don't fight legit values.
    const pageSize =
      typeof rest.pageSize === 'number' && Number.isInteger(rest.pageSize) && rest.pageSize > 0
        ? rest.pageSize
        : DEFAULT_PREFERENCES.pageSize;
    return {
      ...DEFAULT_PREFERENCES,
      ...rest,
      pageSize,
      visibleContent: reconcileVisibleContent({
        storedVisible: rest.visibleContent,
        storedKnown: knownColumns,
        hideableColumns: HIDEABLE_COLUMN_IDS,
        legacyKnownColumns: LEGACY_KNOWN_COLUMNS,
        defaultVisible: DEFAULT_PREFERENCES.visibleContent ?? HIDEABLE_COLUMN_IDS,
      }),
    };
  } catch {
    /* ignore — fall back to defaults */
  }
  return DEFAULT_PREFERENCES;
}

function savePreferences(prefs: CollectionPreferencesProps.Preferences) {
  try {
    // Snapshot the columns that exist NOW alongside the prefs, so a future load
    // can tell "user hid this" from "this didn't exist yet" (reconcileVisibleContent).
    localStorage.setItem(PREFS_STORAGE_KEY, JSON.stringify({ ...prefs, knownColumns: [...HIDEABLE_COLUMN_IDS] }));
  } catch {
    /* ignore */
  }
}

// Drag-resized column widths (id → px), persisted separately from the
// CollectionPreferences object (which has no widths field). Keyed by column id
// (robust to reorder/hide, unlike Cloudscape's positional widths array).
const WIDTHS_STORAGE_KEY = 'ivt_motion_pipeline_col_widths';

function loadColumnWidths(): Record<string, number> {
  try {
    const stored = localStorage.getItem(WIDTHS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Record<string, number>;
      // Keep only known columns with a positive numeric width.
      return Object.fromEntries(
        Object.entries(parsed).filter(
          ([id, w]) => COLUMN_IDS.includes(id as (typeof COLUMN_IDS)[number]) && typeof w === 'number' && w > 0,
        ),
      );
    }
  } catch {
    /* ignore — no persisted widths */
  }
  return {};
}

function saveColumnWidths(widths: Record<string, number>) {
  try {
    localStorage.setItem(WIDTHS_STORAGE_KEY, JSON.stringify(widths));
  } catch {
    /* ignore */
  }
}

interface MotionPipelineListProps {
  /** Permission-allowed industries (the SETTLED permitted set; empty = no industry
   *  permission → empty list). Only meaningful when permissionReady. */
  allowedIndustries?: string[] | null;
  /** True once the permission fetch has succeeded (READY). Gates the pipeline +
   *  Motion-options fetches (fail-CLOSED: on permission error the parent shows an
   *  error and never renders this). Defaults true so the component works standalone. */
  permissionReady?: boolean;
}

export default function MotionPipelineList({
  allowedIndustries,
  permissionReady = true,
}: MotionPipelineListProps = {}) {
  const navigate = useNavigate();
  const [stageCounts, setStageCounts] = useState<SfdcStageCounts | null>(null);
  const [items, setItems] = useState<PipelineListItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Activity popup: the row whose "N 次活动" was clicked (null = closed).
  const [activityRow, setActivityRow] = useState<PipelineListItem | null>(null);

  // Table preferences (page size + visible columns), persisted to localStorage.
  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>(loadPreferences);
  const pageSize = preferences.pageSize ?? 20;

  // Drag-resized column widths (id → px), persisted separately (see helpers).
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(loadColumnWidths);

  // Current UI page (1-based). A client-side window over the loaded `items`.
  const [currentPageIndex, setCurrentPageIndex] = useState(1);

  // Client-side column sort over the LOADED rows. ListTaggedPipelines is keyset
  // (forward-only cursor) with NO server sortBy, so this reorders only what's been
  // fetched: EXACT when the filtered set fits in one FETCH_CHUNK (100), otherwise a
  // best-effort sort of the loaded prefix (a hint is shown while more remains). We
  // hold the sort STATE here and apply it before the page slice below.
  const [sortingColumn, setSortingColumn] = useState<
    TableProps.SortingColumn<PipelineListItem> | undefined
  >(undefined);
  const [sortingDescending, setSortingDescending] = useState(false);

  // Filter selections (all server-side for this tab), persisted in sessionStorage
  // so they survive a tab switch / detail round-trip. The four DOMAIN filters use
  // the SAME keys as the Motion List tab (shared selection across tabs); Motion
  // uses a Pipeline-tab-specific key (independent from the List tab's Motion
  // filter — different semantics: here motionIds go to the backend).
  const [industries, setIndustries] = useMotionFilterState('industries');
  const [useCases, setUseCases] = useMotionFilterState('useCases');
  const [motions, setMotions] = useMotionFilterState('pipeline.motions');
  const [subTopics, setSubTopics] = useMotionFilterState('subTopics');
  const [assets, setAssets] = useMotionFilterState('assets');
  // SFDC property filter — Pipeline-tab only. A PropertyFilter one-level AND/OR tree
  // over Stage / Account / Oppty Name / Opp ID / Owner (replaces the old standalone
  // SFDC Stage Multiselect — Stage is now a field in this tree). Persisted to
  // sessionStorage (survives tab switch / detail round-trip), seeded once at mount.
  const [propertyQuery, setPropertyQueryState] = useState<PropertyFilterProps.Query>(loadPropertyFilterQuery);
  const setPropertyQuery = useCallback((q: PropertyFilterProps.Query) => {
    setPropertyQueryState(q);
    try {
      sessionStorage.setItem(PROPERTY_FILTER_STORAGE_KEY, JSON.stringify(q));
    } catch {
      /* ignore quota / disabled storage */
    }
  }, []);
  // Engagement Depth Score is now a PropertyFilter tree field (PIPELINE_SCORE), not a
  // standalone dropdown — its selection lives inside propertyQuery / the property-filter
  // sessionStorage key, and its candidates come from GetPipelineFilterOptions (DAT-8462
  // Phase 2). No separate score state / option pool here anymore.
  // Motion-claim filter — Pipeline-tab only. undefined = All / true = with claim /
  // false = tag-only. Local (transient) state — not persisted like the option-array
  // filters (it's a tri-state boolean, not a MotionFilterOption[]).
  const [hasMotion, setHasMotion] = useState<boolean | undefined>(undefined);

  // Motion filter OPTIONS come from ListMotions (loaded once).
  const [motionOptions, setMotionOptions] = useState<MotionFilterOption[]>([]);
  useEffect(() => {
    // Gate on permission (same race as the main fetch): don't build the option
    // pool from an unrestricted list before permission settles.
    if (!permissionReady) return;
    let cancelled = false;
    // Empty-scope short-circuit: a zero-permission user's option pool is empty —
    // don't send industries:[] (serializes to no query param, an ambiguous "no filter"
    // request). Correctness/UX guard; the backend enforces industry permission
    // server-side on the LIST ops (P8), so the option pool it returns is already
    // scoped — this just avoids the ambiguous request + a pointless round-trip.
    if (isEmptyIndustryScope([], allowedIndustries ?? [])) {
      setMotionOptions([]);
      return () => {
        cancelled = true;
      };
    }
    (async () => {
      try {
        // Scope the Motion-filter OPTION pool to the user's allowed industries —
        // else Motions from disallowed industries (e.g. ECOM) show up as pickable
        // filter options. The empty-scope case is short-circuited above.
        const out = await getIvtClient().send(
          new ListMotionsCommand({ industries: resolveIndustryQueryScope([], allowedIndustries ?? []) }),
        );
        if (cancelled) return;
        setMotionOptions(
          (out.motions ?? [])
            .filter((m): m is typeof m & { motionId: string } => !!m.motionId)
            .map((m) => ({ value: m.motionId, label: m.name ?? m.motionId }))
            .sort((a, b) => (a.label ?? '').localeCompare(b.label ?? '', 'en')),
        );
      } catch {
        /* non-blocking */
      }
    })();
    return () => {
      cancelled = true;
    };
    // Re-fetch when allowedIndustries resolves (async, starts null) so the option
    // pool drops now-disallowed Motions once permission is known.
  }, [allowedIndustries, permissionReady]);

  // assetId → display name, so the Assets column shows names not opaque ids.
  const [assetNameById, setAssetNameById] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const out = await getIvtClient().send(new ListAssetsCommand({}));
        if (cancelled) return;
        const m = new Map<string, string>();
        for (const a of out.assets ?? []) {
          if (a.assetId) m.set(a.assetId, a.name ?? a.useCase ?? a.assetId);
        }
        setAssetNameById(m);
      } catch {
        /* non-blocking — falls back to showing the id */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const industryValues = useMemo(() => industries.map((o) => o.value).filter((v): v is string => !!v), [industries]);
  const useCaseValues = useMemo(() => useCases.map((o) => o.value).filter((v): v is string => !!v), [useCases]);
  const motionIdValues = useMemo(() => motions.map((o) => o.value).filter((v): v is string => !!v), [motions]);
  const subTopicIdValues = useMemo(() => subTopics.map((o) => Number(o.value)).filter((n) => Number.isFinite(n)), [subTopics]);
  const assetIdValues = useMemo(() => assets.map((o) => o.value).filter((v): v is string => !!v), [assets]);
  // SFDC property tree → JSON-string backend param (queryToPropertyFilterParam does
  // the translate + stringify; undefined when the query is empty → param omitted).
  const propertyFilterParam = useMemo(() => queryToPropertyFilterParam(propertyQuery), [propertyQuery]);

  // SFDC property-filter candidate values (GetPipelineFilterOptions). Scoped to the
  // same IVT filters as the list (industries = permission scope, §4.6) so options
  // don't leak out-of-permission identity values. Gated on permissionReady (same
  // race as the list fetch). Stage prefetched; identity fields search via onLoadItems.
  const optionScope = useMemo(
    () => ({
      industries: resolveIndustryQueryScope(industryValues, allowedIndustries ?? []),
      useCases: useCaseValues,
      subTopicIds: subTopicIdValues,
      assetIds: assetIdValues,
      motionIds: motionIdValues,
      hasMotion,
    }),
    [industryValues, useCaseValues, subTopicIdValues, assetIdValues, motionIdValues, hasMotion, allowedIndustries],
  );
  const pipelineFilterOptions = usePipelineFilterOptions(
    optionScope,
    permissionReady && !isEmptyIndustryScope(industryValues, allowedIndustries ?? []),
  );

  // Keyset pagination (backend §2): ListTaggedPipelines is @paginated. We fetch a
  // CHUNK at a time and APPEND it to `items` (keyset cursors are opaque +
  // forward-only, so there is no random page jump). The table paginates the
  // ACCUMULATED rows client-side; crossing the loaded frontier fetches the next
  // chunk (see onNextPageClick). Two backend 铁律 encoded here:
  //   ① nextToken embeds the filter fingerprint — any filter change MUST reset to
  //      a fresh first page (token=undefined), else the backend 400s. The
  //      filter-change effect below always calls load(reset=true).
  //   ② stageCounts covers the FULL filtered set (not the page) and is stable
  //      across pages, so we just overwrite it from every response — never
  //      accumulate it from the page rows.
  const [nextToken, setNextToken] = useState<string | undefined>(undefined);
  const [loadingMore, setLoadingMore] = useState(false);
  // Load-more failures are shown INLINE next to the button (the page-1 `error`
  // renders in the Table's empty slot, which is never empty after page 1 —
  // otherwise a load-more error would be silent). Cleared on the next attempt.
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);

  // Request-sequence guard against stale responses: every load() bumps this and
  // captures its own seq; when the response lands it only applies if it's still
  // the latest. Prevents an in-flight load-more (old filter) from appending onto
  // a fresh filter-reset if it resolves out of order.
  const loadSeqRef = useRef(0);

  // allowedIndustries is part of the key: it resolves ASYNC (starts null), and it
  // changes the effective industry scope for an empty selection — so when it
  // arrives the list must reset to a fresh first page + refetch (铁律①).
  // Score is folded in via propertyFilterParam now (PIPELINE_SCORE tree field), so it
  // no longer needs its own filterKey segment.
  const filterKey = `${industryValues.join('|')}#${useCaseValues.join('|')}#${motionIdValues.join('|')}#${subTopicIdValues.join(
    '|',
  )}#${assetIdValues.join('|')}#${propertyFilterParam ?? ''}#${String(hasMotion)}#${(allowedIndustries ?? []).join('|')}`;

  // Returns whether this load actually applied a fresh chunk (true) — the caller
  // (next-page click) only advances the page when it did AND it's still current,
  // so a failed / superseded fetch never advances to a non-existent page.
  const load = useCallback(
    async (reset: boolean, token?: string): Promise<boolean> => {
      const seq = ++loadSeqRef.current;
      if (reset) {
        setLoading(true);
        setError(null);
      } else {
        setLoadingMore(true);
        setLoadMoreError(null);
      }
      // Empty-scope short-circuit: an EMPTY resolved scope (zero-permission user, no
      // explicit selection) must NOT be sent as industries:[] — that serializes to no
      // query param, an ambiguous "no filter" request. Clear rows/counts and skip the
      // round-trip. Correctness/UX guard; the backend enforces industry permission
      // server-side on the LIST ops (P8), so it's no longer the security boundary.
      // (isEmptyIndustryScope docstring explains the serialization trap.)
      if (isEmptyIndustryScope(industryValues, allowedIndustries ?? [])) {
        setItems([]);
        setStageCounts(null);
        setNextToken(undefined);
        setLoading(false);
        setLoadingMore(false);
        return true;
      }
      try {
        const out = await getIvtClient().send(
          new ListTaggedPipelinesCommand({
            // Empty selection defaults to the allowed set (not "no filter") so
            // disallowed industries (e.g. ECOM) don't leak. The empty-scope case is
            // short-circuited above, so here the resolved scope is always non-empty
            // and serializes to a real filter. Backend doesn't re-check this phase.
            industries: resolveIndustryQueryScope(industryValues, allowedIndustries ?? []),
            useCases: useCaseValues.length ? useCaseValues : undefined,
            motionIds: motionIdValues.length ? motionIdValues : undefined,
            subTopicIds: subTopicIdValues.length ? subTopicIdValues : undefined,
            assetIds: assetIdValues.length ? assetIdValues : undefined,
            // SFDC property tree (Stage/Account/Oppty/Owner, one-level AND/OR) as a
            // JSON string on the propertyFilter query param; undefined omits it.
            sfdcPipelinePropertyFilter: propertyFilterParam,
            // Motion-claim tri-state: undefined = All (omit) / true = with claim /
            // false = tag-only. Passed straight through to the backend hasMotion param.
            hasMotion,
            // Score is no longer a separate param — it rides inside sfdcPipelinePropertyFilter
            // as a PIPELINE_SCORE tree condition now (DAT-8462 Phase 2; legacy pipelineScores
            // param is @deprecated backend-side and no longer sent).
            maxResults: FETCH_CHUNK,
            // reset → omit token (fresh first page); load-more → pass current cursor.
            nextToken: reset ? undefined : token,
          }),
        );
        if (seq !== loadSeqRef.current) return false; // superseded by a newer load → drop
        setStageCounts(out.stageCounts ?? null); // 铁律②: full-set, overwrite
        // (Score candidates no longer come from out.scoreOptions — the PIPELINE_SCORE
        // PropertyFilter field sources them via GetPipelineFilterOptions; DAT-8462 Ph2.)
        setItems((prev) => (reset ? out.items ?? [] : [...(prev ?? []), ...(out.items ?? [])]));
        setNextToken(out.nextToken); // absent → last page
        return true;
      } catch (e) {
        if (seq !== loadSeqRef.current) return false; // stale error, ignore
        const msg = (e as Error).message ?? 'Unknown error';
        // Page-1 failure → full error state (Table empty slot). Next-chunk failure
        // → inline error + retry; the caller must NOT advance the page.
        if (reset) setError(msg);
        else setLoadMoreError(msg);
        return false;
      } finally {
        if (seq === loadSeqRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    // filterKey stands in for the individual filter arrays (stable-string dep).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterKey],
  );

  // Reset to the first page whenever a filter changes or a manual reload fires.
  // 铁律①: this always resets the token — a filter change never reuses a cursor.
  // Gate on permissionReady: don't fetch until permission settles, else the
  // unrestricted (null→undefined) query races ahead and briefly shows disallowed
  // industries. `loading` stays true (initial) so the table shows a spinner. When
  // permission lands, allowedIndustries flows into filterKey → this re-runs.
  useEffect(() => {
    if (!permissionReady) return;
    load(true);
    setCurrentPageIndex(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey, filterKey, permissionReady]);

  // ── Client-side windowing over the accumulated rows ────────────────────────
  const allItems = items ?? [];
  // Apply the active column sort over the LOADED rows BEFORE the page slice, so
  // the sort spans all loaded pages (not just the visible one). No sort → identity
  // order (server keyset order). Stable copy (never mutate `items`).
  const sortedItems = useMemo(() => {
    const cmp = sortingColumn?.sortingComparator;
    if (!cmp) return allItems;
    // Apply direction INSIDE the comparator (× -1 for descending) rather than
    // sort-then-reverse: with V8's stable sort this keeps equal-key rows in their
    // original (server keyset) order in BOTH directions — matching tab1's
    // useCollection behavior. A trailing .reverse() would instead flip tie-order.
    const dir = sortingDescending ? -1 : 1;
    return [...allItems].sort((a, b) => cmp(a, b) * dir);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allItems, sortingColumn, sortingDescending]);
  // Navigable page count over what's LOADED. If the server still has more
  // (nextToken present), we DON'T expose a trailing PARTIAL page — a
  // less-than-full last page (e.g. 15 loaded, pageSize 10 → 5 leftover) would let
  // Cloudscape treat page 2 as "available" and show a half-page WITHOUT fetching.
  // Using floor while more remains hides that stub; clicking "下一页" then lands on
  // the out-of-range branch → fetches the next chunk → fills a whole page. Once
  // fully loaded (no nextToken), ceil so the final partial page is shown.
  const loadedPages = nextToken
    ? Math.max(1, Math.floor(allItems.length / pageSize))
    : Math.max(1, Math.ceil(allItems.length / pageSize));
  const pageItems = useMemo(
    () => sortedItems.slice((currentPageIndex - 1) * pageSize, currentPageIndex * pageSize),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sortedItems, currentPageIndex, pageSize],
  );

  // Clamp the page if a pageSize change shrank the page count (e.g. 50→10 while
  // on a high page that no longer exists).
  useEffect(() => {
    if (currentPageIndex > loadedPages) setCurrentPageIndex(loadedPages);
  }, [loadedPages, currentPageIndex]);

  // Next-page click. If the requested page is already loaded → just advance
  // (instant, no network). If it's past the loaded frontier and the server has
  // more (nextToken) → fetch the next chunk, and advance ONLY if that fetch
  // actually applied AND nothing superseded it (filter change / pageSize change)
  // in the meantime — otherwise we'd land on a non-existent page. The fetch's own
  // seq guard + the returned success flag + a re-check of loadSeqRef cover the
  // failure, stale-response, and concurrent-pageSize races the reviewer flagged.
  const handleNextPage = (requestedPageIndex: number, requestedPageAvailable: boolean) => {
    if (requestedPageAvailable) {
      setCurrentPageIndex(requestedPageIndex);
      return;
    }
    if (nextToken && !loadingMore) {
      const seqAtClick = loadSeqRef.current + 1; // the seq this load() will take
      void load(false, nextToken).then((applied) => {
        if (applied && loadSeqRef.current === seqAtClick) {
          setCurrentPageIndex(requestedPageIndex);
        }
      });
    }
  };

  const handlePreferencesChange = (detail: CollectionPreferencesProps.Preferences) => {
    setPreferences(detail);
    savePreferences(detail);
    setCurrentPageIndex(1); // page-size change re-slices from the top
  };

  // Column sort click: capture the column + direction and jump back to page 1
  // (the top of the newly-sorted order). Sort is client-side over loaded rows.
  const handleSortingChange = (detail: {
    sortingColumn: TableProps.SortingColumn<PipelineListItem>;
    isDescending?: boolean;
  }) => {
    setSortingColumn(detail.sortingColumn);
    setSortingDescending(!!detail.isDescending);
    setCurrentPageIndex(1);
  };

  // Drag-resize: Cloudscape gives widths POSITIONALLY over the currently-rendered
  // columns, so map them back onto the visible column ids (order-safe under
  // hide/reorder) and persist.
  const handleColumnWidthsChange = (widths: readonly number[]) => {
    setColumnWidths((prev) => {
      const next = { ...prev };
      visibleColumns.forEach((col, i) => {
        if (col.id && typeof widths[i] === 'number') next[col.id] = widths[i];
      });
      saveColumnWidths(next);
      return next;
    });
  };

  // Visible columns = the row header (always) + the user's chosen hideable set.
  // Falls back to all hideable columns if the stored set is empty/absent, so the
  // table can never render with zero columns.
  const chosenHideable =
    preferences.visibleContent && preferences.visibleContent.length > 0
      ? preferences.visibleContent
      : HIDEABLE_COLUMN_IDS;
  const visibleColumnIds = new Set<string>([ROW_HEADER_ID, ...chosenHideable]);

  // Full column set (order = COLUMN_IDS). Cells close over navigate /
  // assetNameById / setActivityRow, so this is built in-render (cheap).
  const allColumns: TableProps.ColumnDefinition<PipelineListItem>[] = [
    {
      id: 'account',
      header: COLUMN_LABELS.account,
      cell: (m) => m.accountName ?? '—',
      isRowHeader: true,
      sortingField: 'accountName',
      sortingComparator: (a, b) => (a.accountName ?? '').localeCompare(b.accountName ?? ''),
    },
    {
      id: 'opp',
      header: COLUMN_LABELS.opp,
      sortingField: 'oppName',
      sortingComparator: (a, b) => (a.oppName ?? '').localeCompare(b.oppName ?? ''),
      // Opportunity → EXTERNAL deep-link to the SFDC Lightning record (its
      // canonical home), new tab. Plain text when no opptyId. (Mirrors
      // MotionDetailPage PipelineTable.)
      cell: (m) =>
        m.opptyId ? (
          <Link href={oppSfdcUrl(m.opptyId)} external target="_blank">
            {m.oppName ?? m.opptyId}
          </Link>
        ) : (
          (m.oppName ?? '—')
        ),
    },
    {
      id: 'motion',
      header: COLUMN_LABELS.motion,
      // One row per Opp: an Opp claimed by N Motions shows all N as compact chips
      // (first few inline, rest behind "+N"), each an in-app link to that Motion's
      // detail page (carry the origin tab so the breadcrumb returns to Pipeline
      // List). Reads `motions` (the one-row-per-Opp field); the scalar
      // motionId/motionName is DEPRECATED. NOT sortable — a row has many Motions,
      // so a single-value sort is meaningless.
      cell: (m) => (
        <OverflowChips
          ariaLabel="Motions"
          items={(m.motions ?? [])
            .filter((mr) => mr.motionId)
            .map((mr) => ({
              key: mr.motionId as string,
              label: mr.motionName ?? (mr.motionId as string),
              onClick: () =>
                navigate(`/ivt/motions/${mr.motionId}`, { state: { fromTab: 'pipeline' } }),
            }))}
        />
      ),
    },
    {
      id: 'assets',
      header: COLUMN_LABELS.assets,
      // assetIds → names (ListAssets map, falls back to id) as compact chips:
      // first few inline, the rest behind "+N". (Chips carry a link slot for
      // when an asset-detail route lands.)
      cell: (m) => (
        <OverflowChips
          ariaLabel="Assets"
          items={(m.assetIds ?? []).map((id) => ({ key: id, label: assetNameById.get(id) ?? id }))}
        />
      ),
    },
    {
      id: 'useCases',
      header: COLUMN_LABELS.useCases,
      // Same compact-chip treatment; UC has no id, so key by industry+name.
      cell: (m) => (
        <OverflowChips
          ariaLabel="Use Cases"
          items={(m.useCases ?? [])
            .filter((u) => u.ucName)
            .map((u) => ({ key: `${u.industry ?? ''}::${u.ucName}`, label: u.ucName as string }))}
        />
      ),
    },
    {
      id: 'topics',
      header: COLUMN_LABELS.topics,
      // Topic-anchored assets have no UC — their classification is the Topic link.
      // Separate column from Use Cases (orthogonal dims; a row's UC and Topic are
      // complementary, rarely both). chip label = "父 · 子" (topicName · subTopicName).
      cell: (m) => (
        <OverflowChips
          ariaLabel="Topics"
          items={(m.topicLinks ?? [])
            .filter((t) => t.subTopicName)
            .map((t) => ({
              key: `${t.subTopicId ?? ''}::${t.subTopicName}`,
              label: t.topicName ? `${t.topicName} · ${t.subTopicName}` : (t.subTopicName as string),
            }))}
        />
      ),
    },
    // SFDC Stage sorted alphabetically on the raw label. NOTE: this is NOT funnel
    // order (Prospect→Launched) — there's no stage→rank map on the row, so a
    // lexical sort is the honest default; a rank-ordered sort is a follow-up.
    {
      id: 'stage',
      header: COLUMN_LABELS.stage,
      cell: (m) => m.sfdcStage ?? '—',
      sortingField: 'sfdcStage',
      sortingComparator: (a, b) => (a.sfdcStage ?? '').localeCompare(b.sfdcStage ?? ''),
    },
    {
      id: 'arr',
      header: COLUMN_LABELS.arr,
      cell: (m) => fmtArr(m.arr),
      sortingField: 'arr',
      sortingComparator: (a, b) => (a.arr ?? 0) - (b.arr ?? 0),
    },
    // Owner = SFDC opportunity owner (oppOwner), NOT the IVT claimer (ownerAlias).
    // beta fill rate is low → mostly "—" until SFDC sync catches up.
    {
      id: 'owner',
      header: COLUMN_LABELS.owner,
      cell: (m) => m.oppOwner ?? '—',
      sortingField: 'oppOwner',
      sortingComparator: (a, b) => (a.oppOwner ?? '').localeCompare(b.oppOwner ?? ''),
    },
    // Score = backend pipelineScore (opp-scoped, @range 0-3). "{n}分" badge colored
    // by the shared score palette (same as the Impact tab activity relevance);
    // score 0 shares score 1's red per product. A score outside the palette (future
    // 0-5) falls back to plain "{n}分" text (no crash). Null → "—".
    {
      id: 'score',
      header: COLUMN_LABELS.score,
      sortingField: 'pipelineScore',
      sortingComparator: (a, b) => (a.pipelineScore ?? -1) - (b.pipelineScore ?? -1),
      cell: (m) => {
        if (m.pipelineScore == null) return '—';
        const s = scoreStyleWithZero(m.pipelineScore);
        const text = `${m.pipelineScore}分`;
        return s ? (
          <span className="ivt-pipeline-score-badge" style={{ background: s.bg, color: s.color }}>
            {text}
          </span>
        ) : (
          <span>{text}</span>
        );
      },
    },
    {
      id: 'activities',
      header: COLUMN_LABELS.activities,
      // Sort by activity count (the meaningful scalar behind the "N 次活动" trigger).
      sortingField: 'activityCount',
      sortingComparator: (a, b) => (a.activityCount ?? 0) - (b.activityCount ?? 0),
      // "N 次活动" — opens the timeline popup IN PLACE, styled as a dashed-underline
      // trigger (button, keyboard-accessible), NOT a blue link. 0 → plain span.
      cell: (m) => {
        const n = m.activityCount ?? 0;
        return n > 0 ? (
          <button type="button" className="ivt-activity-trigger" onClick={() => setActivityRow(m)}>
            {activityCountLabel(n, m.ivtActivityCount)}
          </button>
        ) : (
          <span>{activityCountLabel(0, m.ivtActivityCount)}</span>
        );
      },
    },
    {
      id: 'tags',
      header: COLUMN_LABELS.tags,
      // Grey rounded chips (Figma), same compact "+N" fold as Assets/UC. matchedTags
      // = only the tags that resolve to our tag system (registered UC/asset); why this
      // opp is in the list. Backend dropped the raw full `tags` (dedupe noise).
      cell: (m) => <OverflowChips ariaLabel="Tags" items={(m.matchedTags ?? []).map((t) => ({ key: t, label: t }))} />,
    },
  ];

  // Visible columns, each with a minWidth floor (required for resizable columns)
  // and any persisted drag width applied.
  const visibleColumns: TableProps.ColumnDefinition<PipelineListItem>[] = allColumns
    .filter((c) => visibleColumnIds.has(c.id as string))
    .map((c) => {
      const id = c.id as (typeof COLUMN_IDS)[number];
      return {
        ...c,
        minWidth: COLUMN_MIN_WIDTH[id],
        ...(columnWidths[id] ? { width: columnWidths[id] } : {}),
      };
    });

  return (
    <div className="ivt-motion-pipeline">
      <MotionFilters
        industries={industries}
        onIndustriesChange={setIndustries}
        allowedIndustries={allowedIndustries}
        permissionReady={permissionReady}
        useCases={useCases}
        onUseCasesChange={setUseCases}
        motionOptions={motionOptions}
        motions={motions}
        onMotionsChange={setMotions}
        subTopics={subTopics}
        onSubTopicsChange={setSubTopics}
        assets={assets}
        onAssetsChange={setAssets}
        hasMotion={hasMotion}
        onHasMotionChange={setHasMotion}
      />

      {/* SFDC property filter — one-level AND/OR over Stage / Account / Oppty Name /
          Opp ID / Owner. Full-width row directly under the dropdown filters (design
          §5.1). SFDC Stage lives here now (migrated out of the MotionFilters row). */}
      <PipelinePropertyFilter
        query={propertyQuery}
        onChange={setPropertyQuery}
        filteringOptions={pipelineFilterOptions.filteringOptions}
        filteringStatusType={pipelineFilterOptions.status}
        onLoadItems={pipelineFilterOptions.onLoadItems}
      />

      {/* Pipeline by Stage (Tagged only): per-SFDC-stage KPI cards
          (backend-aggregated). Title uses the same Cloudscape h2 as the table
          below so the two card headers match. */}
      {stageCounts && !error && (
        <div className="ivt-pipeline-info" aria-label="Pipeline by SFDC stage">
          <div className="ivt-pipeline-info__title">
            <Header variant="h2">Pipeline by Stage (Tagged only)</Header>
          </div>
          <div className="ivt-pipeline-info__cells">
            {STAGE_CELLS.map(({ key, label, accent }) => {
              const n = stageCounts[key] ?? 0;
              return (
                <div
                  className="ivt-pipeline-info__cell"
                  key={key}
                  style={{ ['--stage-accent' as string]: accent }}
                >
                  <span className="ivt-pipeline-info__label" title={label}>
                    {label}
                  </span>
                  <span
                    className={`ivt-pipeline-info__count${n === 0 ? ' ivt-pipeline-info__count--zero' : ''}`}
                  >
                    {n}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="ivt-motion-card">
        {/* Client-side sort spans only LOADED rows. When the server still has more
            (nextToken), warn that the active sort covers the loaded prefix, not the
            full set — so a "top by ARR" could still live in an unfetched page. */}
        {sortingColumn && nextToken && (
          <Box margin={{ bottom: 's' }}>
            <Alert type="info">
              排序仅作用于已加载的行。还有更多数据未加载，翻到最后一页可加载全部后再排序以获得完整结果。
            </Alert>
          </Box>
        )}
        <Table<PipelineListItem>
          variant="embedded"
          loading={loading}
          loadingText="加载 Pipeline…"
          header={<Header variant="h2">Pipeline List (Tagged only)</Header>}
          items={pageItems}
          trackBy={(m) => m.opptyId ?? ''}
          // Client-side column sort over LOADED rows (keyset tab → no server sortBy).
          sortingColumn={sortingColumn}
          sortingDescending={sortingDescending}
          onSortingChange={({ detail }) => handleSortingChange(detail)}
          // Drag-resizable column borders; widths persist (onColumnWidthsChange →
          // localStorage). Fixes over-wide cells like a long Opportunity name.
          resizableColumns
          onColumnWidthsChange={({ detail }) => handleColumnWidthsChange(detail.widths)}
          pagination={
            <Pagination
              currentPageIndex={currentPageIndex}
              pagesCount={loadedPages}
              // openEnd: the server may still have more than what's loaded — keep
              // the "next" affordance alive while nextToken is present.
              openEnd={!!nextToken}
              disabled={loadingMore}
              ariaLabels={{
                paginationLabel: 'Pipeline 分页',
                previousPageLabel: '上一页',
                nextPageLabel: '下一页',
                pageLabel: (n) => `第 ${n} 页`,
              }}
              onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
              onNextPageClick={({ detail }) =>
                handleNextPage(detail.requestedPageIndex, detail.requestedPageAvailable)
              }
            />
          }
          preferences={
            <CollectionPreferences
              title="偏好设置"
              confirmLabel="确定"
              cancelLabel="取消"
              preferences={preferences}
              onConfirm={({ detail }) => handlePreferencesChange(detail)}
              pageSizePreference={{ title: '每页条数', options: PAGE_SIZE_OPTIONS }}
              visibleContentPreference={{ title: '显示列', options: VISIBLE_CONTENT_OPTIONS }}
            />
          }
          columnDefinitions={visibleColumns}
          empty={
            error ? (
              <Alert type="error" header="无法加载 Pipeline" action={<Button onClick={() => setReloadKey((k) => k + 1)}>重试</Button>}>
                {error}
              </Alert>
            ) : permissionReady && (allowedIndustries?.length ?? 0) === 0 ? (
              // Zero-permission user: rows are short-circuited to empty (not
              // fetched). Say so explicitly rather than "no tagged Opp", which
              // reads as a genuinely-empty pipeline. Mirrors MotionsPage +
              // the Impact page's 无 Industry 权限 banner.
              <Box textAlign="center" color="inherit" padding={{ vertical: 'l' }}>
                <SpaceBetween size="xs">
                  <b>无 Industry 权限</b>
                  <span>当前账号没有任何 Industry 的查看权限，暂无 Pipeline 可展示。如需权限请联系管理员。</span>
                </SpaceBetween>
              </Box>
            ) : (
              // Results are industry-scoped server-side, so a partially-permitted
              // user (e.g. viewing a GAMES asset's pipeline they can't access) sees
              // an empty table even when Opp exist in industries they lack. Flag
              // that neutrally so the empty ≠ "definitely nothing here".
              <Box textAlign="center" color="inherit" padding={{ vertical: 'l' }}>
                <SpaceBetween size="xs">
                  <b>No tagged pipeline</b>
                  <span>当前筛选下没有已 tag 的 Opp。</span>
                  <Box variant="small" color="text-body-secondary">部分行业的数据可能因权限未展示。</Box>
                </SpaceBetween>
              </Box>
            )
          }
        />

        {/* A load-more (next-chunk fetch) failure surfaces inline here rather
            than silently — the page-1 error goes to the Table's empty slot, which
            is never empty after page 1. */}
        {loadMoreError && (
          <div className="ivt-pipeline-loadmore">
            <span className="ivt-pipeline-loadmore__error" role="alert">
              ⚠️ 加载下一页失败：{loadMoreError}
            </span>
            <Button onClick={() => nextToken && load(false, nextToken)} loading={loadingMore}>
              重试
            </Button>
          </div>
        )}
      </div>

      {activityRow && (
        <MotionActivityPopup
          opptyId={activityRow.opptyId as string}
          title={activityRow.accountName ?? activityRow.oppName ?? (activityRow.opptyId as string)}
          count={activityRow.activityCount ?? 0}
          ivtCount={activityRow.ivtActivityCount}
          onClose={() => setActivityRow(null)}
        />
      )}
    </div>
  );
}
